import { lazy, Suspense } from 'react'
import { Await, createBrowserRouter, redirect, RouterProvider } from 'react-router-dom'

import ErrorBoundary from 'components/error-boundary'
import Layout from 'components/layout'
import Loading from 'components/loading'
import Splash from 'components/splash'
import { useApi } from 'contexts/api-context'
import { getCurrentSession } from 'contexts/user-context'

import AccountProfile, { EditEmail, EditName } from 'screens/account-profile'
import Dashboard from 'screens/dashboard'
import Encoders from 'screens/encoders'
import ForgotPassword from 'screens/forgot-password'
import History from 'screens/history'
import HistoryDetail from 'screens/history-detail'
import Login from 'screens/login'
import CreateVideo from 'screens/create-video'
import ManageVideo from 'screens/manage-video'
import ImageGeneration from 'screens/image-generation'
import InteractiveAvatar from 'screens/interactive-avatar'
import Manage from 'screens/manage'
import ManageDetail from 'screens/manage-detail'
import ResetPassword from 'screens/reset-password'
import Schedule from 'screens/schedule'
import WhatsNew from 'screens/whats-new'
import Rulesets from 'screens/rulesets'
import CustomerSettings from 'screens/customer-settings'
import Graphics from 'screens/graphics'
import GraphicsDetail from 'screens/graphics-detail'
import AddGraphic from 'screens/add-graphic'

const Lazy = lazy(() => import('../screens/lazy/lazy'))

export const FBXSRouterProvider = () => {
  const { isReady } = useApi()
  const router = createBrowserRouter([
    {
      path: '/login',
      errorElement: <ErrorBoundary />,
      element: (
        <Suspense fallback={<Splash showContainer />}>
          <Login />
        </Suspense>
      ),
    },
    {
      path: '/reset-password',
      element: <ResetPassword />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPassword />,
    },
    {
      path: '/lazy',
      element: <Lazy />,
    },
    {
      id: 'root',
      loader: async () => {
        const session = await getCurrentSession()
        if (!session.session) return redirect('/login')
        return null
      },
      element: (
        <Suspense fallback={<Loading mt="32px" size="lg" />}>
          <Layout />
        </Suspense>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: '/',
          element: <Dashboard />,
        },
        {
          path: '/profile',
          element: <AccountProfile />,
        },
        {
          path: '/profile/edit-name',
          element: <EditName />,
        },
        {
          path: '/profile/edit-email',
          element: <EditEmail />,
        },
        {
          path: '/create-video',
          element: <CreateVideo />,
        },
        {
          path: '/manage-video',
          element: <ManageVideo />,
        },
        {
          path: '/image-generation',
          element: <ImageGeneration />,
        },
        {
          path: '/interactive-avatar',
          element: <InteractiveAvatar />,
        },
      ],
    },
  ])

  return (
    <Suspense fallback={<Splash showContainer />}>
      <Await resolve={isReady}>
        <RouterProvider router={router} />
      </Await>
    </Suspense>
  )
}
